const CALLBACK_PATH = '/buyer/canva/callback';

export type EnvSource = Record<string, string | boolean | undefined>;

export interface LocationDescriptor {
  origin?: string;
  hostname?: string;
  port?: string;
  protocol?: string;
}

export type RedirectSource = 'env' | 'derived' | 'localhostFallback';

export interface ResolveRedirectOptions {
  env?: EnvSource;
  location?: LocationDescriptor;
}

export const CANVA_DEFAULT_SCOPES = [
  "app:read",
  "app:write",
  "asset:read",
  "asset:write",
  "brandtemplate:content:read",
  "brandtemplate:meta:read",
  "comment:read",
  "comment:write",
  "design:content:read",
  "design:content:write",
  "design:meta:read",
  "design:permission:read",
  "design:permission:write",
  "folder:read",
  "folder:write",
  "folder:permission:read",
  "folder:permission:write",
  "profile:read",
].join(" ");

function getRuntimeEnv(): EnvSource {
  if (typeof import.meta !== 'undefined' && import.meta?.env) {
    return import.meta.env as EnvSource;
  }
  if (typeof process !== 'undefined' && process?.env) {
    return process.env as EnvSource;
  }
  return {};
}

function getRuntimeLocation(): LocationDescriptor {
  if (typeof window !== 'undefined' && window.location) {
    return {
      origin: window.location.origin,
      hostname: window.location.hostname,
      port: window.location.port,
      protocol: window.location.protocol,
    };
  }

  return {
    origin: 'http://localhost:8080',
    hostname: 'localhost',
    port: '8080',
    protocol: 'http:',
  };
}

function buildOrigin(location: LocationDescriptor): string {
  if (location.origin) {
    return location.origin;
  }

  const protocol = location.protocol ?? 'http:';
  const host = location.hostname ?? 'localhost';
  const port = location.port ? `:${location.port}` : '';

  return `${protocol}//${host}${port}`;
}

function normalizeUrl(url: string): string {
  return url.replace(/\/$/, '');
}

export function getCanvaClientId(env: EnvSource = getRuntimeEnv()): string | undefined {
  const value = env?.VITE_CANVA_CLIENT_ID ?? env?.CANVA_CLIENT_ID;
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  return trimmed.length ? trimmed : undefined;
}

export function isValidCanvaClientId(clientId?: string): boolean {
  return Boolean(clientId && clientId.startsWith('OC-') && clientId.length >= 8);
}

export function getCanvaScopes(env: EnvSource = getRuntimeEnv()): string {
  const scopes = env?.VITE_CANVA_SCOPES;
  if (typeof scopes === 'string' && scopes.trim().length > 0) {
    return scopes.trim();
  }
  return CANVA_DEFAULT_SCOPES;
}

export function resolveRedirectUri(
  options: ResolveRedirectOptions = {}
): { redirectUri: string; source: RedirectSource } {
  const env = options.env ?? getRuntimeEnv();
  const location = options.location ?? getRuntimeLocation();

  const envValueRaw = typeof env?.VITE_CANVA_REDIRECT_URI === 'string'
    ? env.VITE_CANVA_REDIRECT_URI.trim()
    : undefined;

  const baseOrigin = buildOrigin(location);
  const defaultRedirect = `${baseOrigin}${CALLBACK_PATH}`;

  let redirectUri = normalizeUrl(envValueRaw || defaultRedirect);
  let source: RedirectSource = envValueRaw ? 'env' : 'derived';

  const hostname = location.hostname ?? getRuntimeLocation().hostname;
  if (
    hostname === 'localhost' &&
    envValueRaw &&
    !envValueRaw.includes('localhost') &&
    !envValueRaw.includes('127.0.0.1')
  ) {
    const port = location.port || getRuntimeLocation().port || '8080';
    redirectUri = `http://localhost:${port}${CALLBACK_PATH}`;
    source = 'localhostFallback';
  }

  return { redirectUri, source };
}

export function getCanvaEnvDiagnostics(
  env: EnvSource = getRuntimeEnv(),
  location: LocationDescriptor = getRuntimeLocation()
) {
  const clientId = getCanvaClientId(env);
  const scopes = getCanvaScopes(env);
  const { redirectUri, source } = resolveRedirectUri({ env, location });

  return {
    clientIdPrefix: clientId ? clientId.substring(0, 8) : null,
    clientIdValid: isValidCanvaClientId(clientId),
    hasClientId: Boolean(clientId),
    redirectUri,
    redirectSource: source,
    scopesCount: scopes.split(' ').length,
  };
}

